import { createContext, useContext, useMemo } from "react"
import {
  useDeclarationConditionFields,
  useDeclarationFramework,
  useDeclarationMemoryHints,
} from "../../../../api/declarationsApi"
import type { DeclarationConditionFields } from "../../../../api/types"

/**
 * STEP C3-b2: HOW THIS REVIEW PAGE READS, fixed once per page mount.
 *
 * `paged` — the page reads one regulation at a time (`/frameworks/{fw}` slices +
 * `/open-items`) instead of the four full reads. Decided once by `useStudioV2State`
 * when the page mounts and never switched mid-session: flags render from cache and
 * then refresh, and swapping data sources under an open editor would drop its state.
 * `activeFramework` — the regulation on screen, whose slice the editor parts read.
 *
 * Default `{ paged: false }`: every surface outside the review page, and every test
 * that renders a part on its own, reads exactly as before.
 */
export interface ReviewMode {
  paged: boolean
  activeFramework: string | null
  /** Step C4's paged article tables (`MC_REVIEW_PAGED`), as fixed at page mount.
   *  Absent outside the review page: a part rendered on its own reads the live
   *  flag (`useReviewPaged`), as before. */
  pagedTables?: boolean
}

const ReviewModeContext = createContext<ReviewMode>({
  paged: false,
  activeFramework: null,
})

export const ReviewModeProvider = ReviewModeContext.Provider

export function useReviewMode(): ReviewMode {
  return useContext(ReviewModeContext)
}

type ConditionRowsRead = {
  data: DeclarationConditionFields | null | undefined
  isLoading: boolean
  isError: boolean
}

function useFullConditionRows(declId: string): ConditionRowsRead {
  const full = useDeclarationConditionFields(declId)
  return { data: full.data, isLoading: full.isLoading, isError: full.isError }
}

function useSliceConditionRows(
  declId: string,
  framework: string | null,
): ConditionRowsRead {
  const slice = useDeclarationFramework(declId, framework)
  const hints = useDeclarationMemoryHints(declId)
  const sliceData = slice.data
  const hintsData = hints.data
  const data = useMemo(
    () =>
      sliceData
        ? {
            declarationId: declId,
            rows: sliceData.conditionRows,
            memory: hintsData ?? [],
          }
        : undefined,
    [declId, sliceData, hintsData],
  )
  return { data, isLoading: slice.isLoading, isError: slice.isError }
}

/**
 * The condition rows an editor part reads, in `/condition-fields`' shape.
 *
 * Flag off: `/condition-fields`, exactly as today (the same single hook call, so a
 * part rendered on its own reads as before). Paged: the open regulation's slice rows
 * (its own framework plus the framework-less ones — the slice filter's rule) and the
 * declaration-wide memory hints. The save paths write into both caches (C3-b1), so an
 * edit shows at once either way.
 *
 * The branch picks a different hook, which is safe ONLY because `paged` is fixed for
 * the whole page mount (`ReviewMode`'s contract): it can never flip under a mounted
 * part, so each part always calls the same hooks in the same order.
 */
export function useConditionRows(declId: string): ConditionRowsRead {
  const { paged, activeFramework } = useReviewMode()
  // eslint-disable-next-line react-hooks/rules-of-hooks -- fixed per page mount
  if (paged) return useSliceConditionRows(declId, activeFramework)
  // eslint-disable-next-line react-hooks/rules-of-hooks -- fixed per page mount
  return useFullConditionRows(declId)
}
